import { useState, useEffect, useMemo } from 'react';

import { AutocompleteOption } from '@occc-app/design-system';


import { OfferTableType } from '../../../../tobacco-api-offers.types';
import { useTobaccoApiOfferBrandsSearch } from '../../../../hooks/use-tobacco-api-offer-brands.hook';

type UseBrandAutocompleteParams = {
  tableType: OfferTableType;
  isOpen: boolean;
  selectedBrand: string;
  onBrandChange: (brand: string) => void;
};

export const useBrandAutocomplete = ({
  tableType,
  isOpen,
  selectedBrand,
  onBrandChange,
}: UseBrandAutocompleteParams) => {
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSearchInput(selectedBrand);
    }
  }, [isOpen, selectedBrand]);

  const { brands, loading, error } = useTobaccoApiOfferBrandsSearch({
    tableType,
    searchInput,
    isOpen,
  });

  const optionsFromApi: AutocompleteOption[] = brands.map((brand) => ({
    value: brand,
    label: brand,
  }));

  const selectedOption: AutocompleteOption | null = selectedBrand
    ? optionsFromApi.find((o) => o.value === selectedBrand) ?? {
        value: selectedBrand,
        label: selectedBrand,
      }
    : null;

  const options = useMemo(() => {
    if (!selectedOption) return optionsFromApi;
    if (optionsFromApi.some((o) => o.value === selectedOption.value)) return optionsFromApi;
    return [selectedOption, ...optionsFromApi];
  }, [optionsFromApi, selectedOption]);

  const handleChange = (_: unknown, newValue: unknown) => {
    const option = Array.isArray(newValue) ? null : (newValue as AutocompleteOption | null);
    onBrandChange(option?.value ?? '');
    setSearchInput(option?.label ?? '');
  };

  const handleInputChange = (_: unknown, value: string, reason: string) => {
    if (reason === 'reset') return;
    setSearchInput(value);
  };

  return {
    options,
    selectedOption,
    searchInput,
    loading,
    error,
    handleChange,
    handleInputChange,
  };
};
